import { action, makeObservable, observable } from 'mobx';

import { UIElementContentSource } from '@smart-tv/hooks/use-ui-element-content/use-ui-element-content';
import {
  ContentItem,
  ContentListProviders,
  UIElement,
  UIElementContent
} from '@smart-tv/typings/content';
import getContentList from '@smart-tv/utils/kmm/getContentList';
import getUiElementContent from '@smart-tv/utils/kmm/getUiElementContent';
import { subscribeUiElementsFlow } from '@smart-tv/utils/kmm/getUiElements';
import type { FlowSubscription } from '@smart-tv/utils/kmm/kmm';
import { Area } from '@norlys-a-s/norlys-play-kmm';
import { BaseError, CmdError } from '@smart-tv/contexts/error-context/error-context';

export interface UIElementExtended extends UIElement {
  hasItems: boolean;
  content?: UIElementContent;
}

export class StripesBaseStore {
  uiElements: UIElementExtended[] = [];
  loading = true;
  error: CmdError | BaseError | null = null;
  activeUIElement: UIElement | null = null;
  stripeUpdateCallbacks: Map<number, Array<(item: ContentItem, action: 'add' | 'remove') => void>> =
    new Map();
  stripesToRefetch: Set<number> = new Set();

  private uiElementsSubscription: FlowSubscription | null = null;
  private uiElementsActive: { current: boolean } | null = null;

  constructor() {
    makeObservable(this, {
      uiElements: observable,
      loading: observable,
      error: observable,
      activeUIElement: observable,
      stripesToRefetch: observable,
      setUiElements: action,
      updateUiElementContent: action,
      setActiveUIElement: action,
      setLoading: action,
      setError: action,
      resetUiElements: action,
      addStripeToRefetch: action,
      clearStripesToRefetch: action
    });
  }

  registerStripeUpdateCallback = (
    uiElementId: number,
    callback: (item: ContentItem, action: 'add' | 'remove') => void
  ) => {
    const callbacks = this.stripeUpdateCallbacks.get(uiElementId) || [];
    callbacks.push(callback);
    this.stripeUpdateCallbacks.set(uiElementId, callbacks);
  };

  unregisterStripeUpdateCallback = (
    uiElementId: number,
    callbackToRemove: (item: ContentItem, action: 'add' | 'remove') => void
  ) => {
    const callbacks = this.stripeUpdateCallbacks.get(uiElementId);
    if (callbacks) {
      const remainingCallbacks = callbacks.filter((callback) => callback !== callbackToRemove);
      if (remainingCallbacks.length > 0) {
        this.stripeUpdateCallbacks.set(uiElementId, remainingCallbacks);
      } else {
        this.stripeUpdateCallbacks.delete(uiElementId);
      }
    }
  };

  updateFavoritesStripe = (item: ContentItem, updateAction: 'add' | 'remove') => {
    const favoritesStripe = this.uiElements.find(
      (uiElement) => uiElement.properties?.update_on_favorite
    );

    if (favoritesStripe) {
      const callbacks = this.stripeUpdateCallbacks.get(favoritesStripe.id);
      if (callbacks) {
        callbacks.forEach((update) => update(item, updateAction));
      }
    }
  };

  async refetchUpdateOnFavoriteStripes(favoritesListId?: number): Promise<void> {
    const updateOnFavoriteStripes = this.uiElements.filter(
      (uiElement) => uiElement.properties?.update_on_favorite
    );

    // Mark stripes for refetch - the hook will handle the actual API call
    updateOnFavoriteStripes.forEach((stripe) => {
      this.addStripeToRefetch(stripe.id);
    });
  }

  setLoading = (payload: boolean) => {
    this.loading = payload;
  };

  setError = (error: CmdError | BaseError | null) => {
    this.error = error;
  };

  setUiElements = (payload: UIElementExtended[]) => {
    this.uiElements = payload;
  };

  updateUiElementContent = (uiElementId: number, content: UIElementContent) => {
    const uiElement = this.uiElements.find((element) => element.id === uiElementId);
    if (uiElement) {
      uiElement.content = content;
    }
  };

  setActiveUIElement = (payload: UIElement | null) => {
    this.activeUIElement = payload;
  };

  resetUiElements = () => {
    this.uiElements = [];
  };

  addStripeToRefetch = (stripeId: number) => {
    this.stripesToRefetch.add(stripeId);
  };

  clearStripesToRefetch = () => {
    this.stripesToRefetch.clear();
  };

  protected async enrichUiElement(
    uiElement: UIElement,
    area: Area,
    favoritesListId?: number
  ): Promise<UIElementExtended> {
    let stripeHasItems = false;
    let content: UIElementContent | undefined;

    const contentSource = uiElement.contentSource;
    const id = uiElement.id;

    if (contentSource === UIElementContentSource.UIelements || contentSource === undefined) {
      const response = await getUiElementContent(Number(id), favoritesListId, area);

      stripeHasItems = response.items.length > 0;
      content = response;
    }

    if (
      contentSource === UIElementContentSource.Universe &&
      uiElement.providerContentProps &&
      uiElement.style
    ) {
      const response = await getContentList(
        uiElement.providerContentProps.universeId,
        ContentListProviders.Subscribed,
        uiElement.providerContentProps.mainGenreId,
        uiElement.providerContentProps.subGenreId,
        uiElement.style,
        9
      );

      stripeHasItems = response.items.length > 0;
      content = response;
    }

    return {
      ...uiElement,
      hasItems: stripeHasItems,
      content
    };
  }

  protected subscribeUiElementsBase(
    favoritesListId: number | undefined,
    area: Area = Area.Frontpage,
    handleEmission: (uiElements: UIElement[], isStale: () => boolean) => Promise<void> | void
  ): Promise<void> {
    this.stopUiElementsSubscription();
    this.setLoading(true);

    // using object here over boolean to keep reference in closure. otherwise the reference is only tied to the first call
    const active = { current: true };
    this.uiElementsActive = active;

    return new Promise<void>((resolveFirst, reject) => {
      this.uiElementsSubscription = subscribeUiElementsFlow(
        favoritesListId,
        area,
        async (uiElements) => {
          const isStale = () => !active.current;
          try {
            await handleEmission(uiElements, isStale);
          } catch (error) {
            if (isStale()) return;
            reject(error);
            return;
          }
          if (isStale()) return;
          resolveFirst();
        }
      );
    });
  }

  stopUiElementsSubscription = () => {
    if (this.uiElementsActive) this.uiElementsActive.current = false;
    this.uiElementsActive = null;
    this.uiElementsSubscription?.cancel();
    this.uiElementsSubscription = null;
  };
}
